/**
 * PHARMAKON — Trésorerie · M4, the pure rules of « Ma caisse » (TWP-22; design/ux/02 §13, TA-75).
 *
 * The server serves and decides everything (`GET /comptoirs/moi`: the caller's open comptoir, his
 * drawer — null + `masque` under the comptage à l'aveugle —, the destinations of HIS role with their
 * « immédiat / à valider » words and their default motif, the motifs per family, the demands still
 * waiting, the last movements, the bounds; `POST /comptoirs/moi/operations/apercu`: the refusals in the
 * engine's words, his drawer before / after, the dialog's two sentences). This module only shapes the
 * options, the bodies and the client key, picks the default motif, says whether a précision is due
 * (from the served rule), and reads the difference between two readings of the card (the receiving
 * toast, UX 02 §13.4). Money stays a decimal string; nothing here adds or subtracts an amount (P-5).
 */
import { especesMasquees } from "@/lib/visibilite-caissier.js";
import { toDecimalString } from "@/lib/number-format.js";
import { CLE_TRESORERIE_JOKER, FAMILLES_CLES_TRESORERIE as F, cleTresorerie } from "@/config/tresorerie.config.js";

/** The route of « Mouvements de ma caisse » and its operation drawer (L1 URL grammar `?panneau=`). */
export const ROUTE_MA_CAISSE = "/ma-tresorerie";
export const routeOperationMaCaisse = (id) => `${ROUTE_MA_CAISSE}?panneau=operation:${id}`;

/**
 * The counter-only roles (the server's `ROLES_TEMPS_REEL_COMPTOIR`): they may operate a till
 * (`tresorerie:create`) and do not read the officine's treasury (`tresorerie_pharmacie:read`) — they
 * hear only their comptoir's frames and get the receiving toast (UX 02 §13.4).
 * @param {object|null} user  @param {(user: object, resource: string, action: string) => boolean} can
 */
export const estRoleComptoirSeulement = (user, can) => Boolean(user) && can(user, "tresorerie", "create") && !can(user, "tresorerie_pharmacie", "read");

/** The motif families the server serves (`motifs.transfert`, `motifs.prelevement`). */
export const FAMILLES_MOTIFS = Object.freeze({ TRANSFERT: "transfert", PRELEVEMENT: "prelevement" });

/** The type of a destination: an account → TRANSFERT, the owner's prélèvement → DECAISSEMENT. */
export const TYPES_GESTE = Object.freeze({ TRANSFERT: "TRANSFERT", DECAISSEMENT: "DECAISSEMENT" });
export const STATUTS = Object.freeze({ BROUILLON: "BROUILLON", CONFIRME: "CONFIRME", ANNULE: "ANNULE" });
export const SENS = Object.freeze({ ENTREE: "ENTREE", SORTIE: "SORTIE" });

/** The destination list's digit shortcuts stop at 9 (UX 02 §13.3 « Digits 1-9 choose »). */
export const RANG_CHIFFRE_MAX = 9;

// ─── The card ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The state of the card from one reading of `GET /comptoirs/moi`.
 * @returns {{ ouverte: boolean, raison: string|null, comptoir: object|null, session: object|null,
 *   attendues: string|null, aveugle: boolean, masquePolitique: boolean, destinations: object[], derniers: object[],
 *   demandes: { a_accuser: object[], en_attente: object[] } }}
 */
export function etatCarte(moi) {
  const ouverte = Boolean(moi?.session && moi?.comptoir && !moi?.raison);
  return {
    ouverte,
    raison: moi?.raison?.message ?? null,
    comptoir: moi?.comptoir ?? null,
    session: moi?.session ?? null,
    attendues: moi?.especes?.attendues ?? null,
    aveugle: Boolean(moi?.especes?.masque),
    // Trésorerie TWP-60 — hidden by the officine (never revealed), not merely blind until the close.
    masquePolitique: especesMasquees(moi),
    destinations: ouverte ? moi.destinations ?? [] : [],
    derniers: moi?.derniers ?? [],
    demandes: { a_accuser: moi?.demandes?.a_accuser ?? [], en_attente: moi?.demandes?.en_attente ?? [] },
  };
}

/**
 * The keys the card refetches on (TWP-11 store): once his comptoir is known, its account, its legs
 * and his session; before (no session read yet / none open), the comptoir and session families — a
 * CAISSIER only ever receives the frames of HIS comptoir (TA-75 (10)), so the family is his.
 */
export function dependancesMonComptoir(moi) {
  const compte = moi?.comptoir?.id;
  const session = moi?.session?.id;
  if (compte && session) return [cleTresorerie(F.COMPTE, compte), cleTresorerie(F.ECRITURES, compte), cleTresorerie(F.SESSION, session)];
  return [cleTresorerie(F.COMPTE, CLE_TRESORERIE_JOKER), cleTresorerie(F.SESSION, CLE_TRESORERIE_JOKER)];
}

// ─── The transfer dialog ───────────────────────────────────────────────────────────────────────────

/** A destination by its key (`cle`: an account id or `PRELEVEMENT_PROPRIETAIRE`). */
export const destinationDe = (moi, cle) => (moi?.destinations ?? []).find((d) => d.cle === cle) ?? null;

/**
 * The RadioGroup options of the destinations — names and the server's access words only, never a
 * balance (§37, AC-M4-02); an open comptoir says who holds it.
 * @param {object[]} destinations
 * @param {(caissier: string|null) => string} ouvertPar the « ouvert (Mme K.) » words
 */
export function optionsDestinations(destinations, ouvertPar) {
  return (destinations ?? []).map((d) => ({
    value: d.cle,
    label: d.ouvert?.ouvert ? `${d.libelle} — ${ouvertPar(d.ouvert.caissier ?? null)}` : d.libelle,
    hint: d.mention,
  }));
}

/** The motifs of a destination's family, in the catalogue's order (Select options). */
export function motifsDe(moi, destination) {
  if (!destination) return [];
  return (moi?.motifs?.[destination.famille_motifs] ?? []).map((m) => ({ value: m.id, label: m.libelle, code: m.code, precision_requise: Boolean(m.precision_requise) }));
}

/** The empty form of one opening of the dialog. */
export const formTransfertInitial = () => ({ destination: null, montant: null, motif_id: null, motifChoisi: false, note: "", bon: false });

/**
 * A destination chosen: its default motif is pre-selected (UX 02 §13.3) unless the user picked a
 * motif himself that the new destination's family still offers.
 */
export function choisirDestination(form, cle, moi) {
  const destination = destinationDe(moi, cle);
  const motifs = motifsDe(moi, destination);
  const garde = form.motifChoisi && motifs.some((m) => m.value === form.motif_id);
  const motif_id = garde ? form.motif_id : destination?.motif_defaut_id ?? motifs[0]?.value ?? null;
  return { ...form, destination: cle, motif_id, motifChoisi: garde ? form.motifChoisi : false };
}

/** The motif chosen by the user (it then survives a change of destination within its family). */
export const choisirMotif = (form, motif_id) => ({ ...form, motif_id, motifChoisi: true });

/** Is the « Précision » due with this motif (TD-183: the served rule — « Divers » only)? */
export function precisionRequise(moi, destination, motif_id) {
  const m = motifsDe(moi, destination).find((x) => x.value === motif_id);
  if (m) return m.precision_requise;
  return false;
}

/** Is the typed précision long enough when it is due (the served minimum)? */
export function precisionSuffisante(note, { requise, min }) {
  if (!requise) return true;
  return String(note ?? "").trim().length >= Number(min ?? 0);
}

/** The body of the advisory preview: only what is typed (the answer names what is missing). */
export function corpsApercuTransfert(form) {
  const note = String(form.note ?? "").trim();
  const montant = toDecimalString(form.montant);
  return {
    ...(form.destination ? { destination: form.destination } : {}),
    ...(montant ? { montant } : {}),
    ...(form.motif_id ? { motif_id: form.motif_id } : {}),
    ...(note ? { note } : {}),
  };
}

/** The body of `POST /comptoirs/moi/operations` — never a source, a type, a statut or a mode (strict server). */
export function corpsTransfert(form, cle) {
  return { ...corpsApercuTransfert(form), idempotency_key: cle };
}

/** One client key per dialog opening: `comptoir:<session>:<uuid>` (a double F9 is a replay — TA-16). */
export const cleTransfert = (sessionId, uuid) => `comptoir:${sessionId}:${uuid}`;

/** The preview's refusals by field (`destination`, `montant`, `motif_id`, `note`) and the general ones. */
export function erreursApercu(apercu) {
  const parChamp = {};
  const generales = [];
  for (const e of apercu?.erreurs ?? []) {
    if (e.champ && !parChamp[e.champ]) parChamp[e.champ] = e.message;
    else if (!e.champ) generales.push(e.message);
  }
  return { parChamp, generales };
}

/** May the gesture be sent? Everything required is typed and the up-to-date preview refused nothing. */
export function peutTransferer(form, apercu, { precisionOk = true } = {}) {
  if (!form.destination || !toDecimalString(form.montant) || !form.motif_id || !precisionOk) return false;
  return Boolean(apercu?.valide) && (apercu.erreurs ?? []).length === 0;
}

/** A gesture to a destination « à valider » (or in transit) leaves the drawer unchanged: the demand words. */
export const estDemande = (destination) => Boolean(destination && destination.statut_cible === STATUTS.BROUILLON);

/** The toast's words after the write: transfer, demand to validate, or transit to acknowledge. */
export function issueEcriture(reponse) {
  if (reponse?.rejoue) return "rejoue";
  if (reponse?.demande?.etat === "EN_TRANSIT") return "transit";
  if (reponse?.operation?.statut === STATUTS.BROUILLON) return "demande";
  return "confirme";
}

/** Is there a bon to print (a transfer, confirmed or still waiting — never the owner's prélèvement)? */
export const bonImprimable = (reponse) => Boolean(reponse?.bon_transfert && reponse?.operation?.id);

// ─── The receiving side (UX 02 §13.4) ──────────────────────────────────────────────────────────────

/**
 * What arrived between two readings of the card: transfers received (confirmed TRANSFERT entries of
 * someone else, in the last movements) and transits to acknowledge (`demandes.a_accuser`). The first
 * reading (no `avant`) and a change of session announce nothing — only what happened while watching.
 * @returns {Array<{ genre: "recu"|"transit", id: string, ligne: object }>}
 */
export function nouveautesReception(avant, apres, { moi } = {}) {
  if (!avant || !apres?.session?.id || avant.session?.id !== apres.session.id) return [];
  const vus = new Set([...(avant.derniers ?? []).map((l) => l.id), ...(avant.demandes?.a_accuser ?? []).map((d) => d.id)]);
  const out = [];
  for (const l of apres.derniers ?? []) {
    if (vus.has(l.id)) continue;
    if (l.type !== TYPES_GESTE.TRANSFERT || l.sens !== SENS.ENTREE || l.statut !== STATUTS.CONFIRME || l.annulee) continue;
    if (moi && l.saisi_par?.id === moi) continue;
    out.push({ genre: "recu", id: l.id, ligne: l });
  }
  for (const d of apres.demandes?.a_accuser ?? []) {
    if (!vus.has(d.id)) out.push({ genre: "transit", id: d.id, ligne: d });
  }
  return out;
}

// ─── « Mouvements de ma caisse » (UX 02 §13.5) ─────────────────────────────────────────────────────

/** The query of one page: his session (default: the server's — open, else last), keyset `apres`. */
export function paramsMaCaisse({ caisse_id = null, limite, apres = null } = {}) {
  return { ...(caisse_id ? { caisse_id } : {}), ...(limite ? { limite } : {}), ...(apres ? { apres } : {}) };
}

/** The « Session » filter: his own sessions, newest first (the server's list — TD-22). */
export function optionsSessions(sessions, libelle) {
  return (sessions ?? []).map((s) => ({ value: s.id, label: libelle(s) }));
}

/** « Depuis ⟶ Vers » of a row (the server's names; « Hors trésorerie » for the owner's prélèvement). */
export function fluxLigne(ligne, horsTresorerie) {
  return { depuis: ligne.depuis?.libelle ?? horsTresorerie, vers: ligne.vers?.libelle ?? horsTresorerie };
}

/** A row's amount reads signed by its effect on HIS drawer (+ received, − sent); a waiting demand unsigned. */
export function montantLigne(ligne) {
  if (ligne.masque || ligne.montant == null) return { masque: true, valeur: null, signe: false, barre: false };
  if (ligne.annulee) return { masque: false, valeur: ligne.montant, signe: false, barre: true };
  if (ligne.effet == null) return { masque: false, valeur: ligne.montant, signe: false, barre: false };
  return { masque: false, valeur: ligne.effet, signe: true, barre: false };
}
